import { processFile, shouldUseMultipart, FileSlice } from './chunk'
import {
    initiateUpload,
    completeUpload,
    abortUpload,
    UploadPartUrl,
    CompletedPart,
    InitiateUploadRequest,
    S3ContentType,
} from '../api/s3Uploads'
import { UploadType } from '../api/uploads'
import { ContentData } from '../cloudinary'

export type S3UploadErrorKind = 'network' | 'server' | 'validation' | 'aborted' | 'unknown'

export class S3UploadError extends Error {
    kind: S3UploadErrorKind
    status?: number

    constructor(kind: S3UploadErrorKind, message: string, status?: number) {
        super(message)
        this.name = 'S3UploadError'
        this.kind = kind
        this.status = status
    }
}

export interface S3UploadOptions {
    onProgress?: (percent: number) => void
    signal?: AbortSignal
}

export const getS3ContentType = (file: File): S3ContentType => (file.type.startsWith('video/') ? 'video' : 'image')

// extension without the dot, lower-cased; falls back to the MIME subtype (e.g. 'image/png' -> 'png')
export const getFileFormat = (file: File): string => {
    const dot = file.name.lastIndexOf('.')
    if (dot > 0 && dot < file.name.length - 1) {
        return file.name.slice(dot + 1).toLowerCase()
    }
    const slash = file.type.indexOf('/')
    return slash >= 0 ? file.type.slice(slash + 1).toLowerCase() : ''
}

const MAX_PART_ATTEMPTS = 3
const RETRY_BASE_DELAY_MS = 500

const getConcurrency = (): number => {
    // floor: a fractional value would make `new Array(n)` throw RangeError
    const configured = Math.floor(Number(process.env.REACT_APP_UPLOAD_CONCURRENCY))
    return Number.isFinite(configured) && configured >= 1 ? configured : 4
}

const wait = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

const classifyApiError = (error: any): S3UploadError => {
    const status = error && error.status !== undefined ? error.status : undefined
    if (
        error &&
        (error.problem === 'NETWORK_ERROR' || error.problem === 'TIMEOUT_ERROR' || error.problem === 'CONNECTION_ERROR')
    ) {
        return new S3UploadError('network', 'Could not connect to the server', status)
    }
    if (typeof status === 'number' && status >= 500) {
        return new S3UploadError('server', 'The server failed to process the upload', status)
    }
    if (status === 400) {
        return new S3UploadError('validation', 'File was rejected, likely too large or an unsupported type', status)
    }
    return new S3UploadError('unknown', 'An unknown error occurred while uploading', status)
}

interface PutPartResult {
    etag: string
}

const putPartToS3 = (
    url: string,
    body: Blob,
    onPartProgress: (loaded: number) => void,
    signal?: AbortSignal,
): Promise<PutPartResult> => {
    return new Promise<PutPartResult>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', url, true)

        const onAbort = () => {
            xhr.abort()
        }

        if (signal) {
            if (signal.aborted) {
                reject(new S3UploadError('aborted', 'Upload was aborted'))
                return
            }
            signal.addEventListener('abort', onAbort)
        }

        const cleanup = () => {
            if (signal) {
                signal.removeEventListener('abort', onAbort)
            }
        }

        xhr.upload.onprogress = event => {
            if (event.lengthComputable) {
                onPartProgress(event.loaded)
            }
        }

        xhr.onload = () => {
            cleanup()
            if (xhr.status >= 200 && xhr.status < 300) {
                const etag = xhr.getResponseHeader('ETag')
                if (!etag) {
                    reject(
                        new S3UploadError(
                            'unknown',
                            'S3 part upload succeeded but no ETag header was returned. This almost always means the bucket CORS config is missing ExposeHeaders: ["ETag"] — check the S3 bucket CORS policy.',
                        ),
                    )
                    return
                }
                onPartProgress(body.size)
                resolve({ etag })
                return
            }
            if (xhr.status >= 500) {
                reject(new S3UploadError('server', `S3 part upload failed with status ${xhr.status}`, xhr.status))
                return
            }
            reject(new S3UploadError('unknown', `S3 part upload failed with status ${xhr.status}`, xhr.status))
        }

        xhr.onerror = () => {
            cleanup()
            reject(new S3UploadError('network', 'Network error while uploading a file part'))
        }

        xhr.onabort = () => {
            cleanup()
            reject(new S3UploadError('aborted', 'Upload was aborted'))
        }

        xhr.send(body)
    })
}

const uploadPartWithRetry = async (
    slice: FileSlice,
    partUrl: UploadPartUrl,
    onPartProgress: (loaded: number) => void,
    signal?: AbortSignal,
): Promise<CompletedPart> => {
    let lastError: unknown
    for (let attempt = 1; attempt <= MAX_PART_ATTEMPTS; attempt++) {
        if (signal && signal.aborted) {
            throw new S3UploadError('aborted', 'Upload was aborted')
        }
        try {
            const { etag } = await putPartToS3(partUrl.url, slice.data, onPartProgress, signal)
            return { part_number: partUrl.part_number, etag }
        } catch (error) {
            lastError = error
            // only transient failures are worth retrying; 4xx (expired URL, EntityTooSmall), a
            // missing ETag (CORS misconfig) and user aborts fail the same way every attempt
            const isRetryable = error instanceof S3UploadError && (error.kind === 'network' || error.kind === 'server')
            if (!isRetryable) {
                throw error
            }
            if (attempt < MAX_PART_ATTEMPTS) {
                onPartProgress(0)
                await wait(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1))
            }
        }
    }
    throw lastError
}

const runWithConcurrency = async <T>(
    items: T[],
    limit: number,
    worker: (item: T, index: number) => Promise<void>,
    signal: AbortSignal,
) => {
    let cursor = 0
    const runNext = async (): Promise<void> => {
        // a sibling worker failed (or the caller cancelled): stop pulling new items
        if (signal.aborted) {
            return
        }
        const currentIndex = cursor
        cursor += 1
        if (currentIndex >= items.length) {
            return
        }
        await worker(items[currentIndex], currentIndex)
        await runNext()
    }
    const poolSize = Math.min(limit, items.length)
    const pool = new Array(poolSize).fill(null).map(() => runNext())
    await Promise.all(pool)
}

export const uploadFileToS3 = async (
    file: File,
    presetType: UploadType,
    options: S3UploadOptions = {},
): Promise<ContentData> => {
    const { onProgress, signal } = options

    const throwIfAborted = () => {
        if (signal && signal.aborted) {
            throw new S3UploadError('aborted', 'Upload was aborted')
        }
    }

    throwIfAborted()

    const useMultipart = shouldUseMultipart(file.size)
    // file.slice() yields a Blob with an empty type, so the browser sends no Content-Type on the
    // presigned PUT. The backend presigns without one, and a Content-Type header the signature
    // did not cover fails with SignatureDoesNotMatch. processFile() slices the same way.
    const slices = useMultipart
        ? processFile(file)
        : [{ data: file.slice(0, file.size), start: 0, end: file.size - 1, size: file.size }]

    const contentType = getS3ContentType(file)
    const initiatePayload: InitiateUploadRequest = {
        content_type: contentType,
        preset_type: presetType,
        file_format: getFileFormat(file),
        parts_count: slices.length,
    }

    const initiateResponse = await initiateUpload(initiatePayload)
    if (!initiateResponse.ok || !initiateResponse.data || !initiateResponse.data.data) {
        throw classifyApiError(initiateResponse)
    }
    const { upload_id: uploadId, key, parts } = initiateResponse.data.data

    const doAbort = async () => {
        try {
            await abortUpload({ upload_id: uploadId, key })
        } catch {
            // best-effort, swallow errors so the real failure surfaces to the caller
        }
    }

    // The multipart upload already exists server-side from this point on, so every failure
    // path below must abort it or the parts are orphaned and billed indefinitely.
    if (parts.length !== slices.length) {
        await doAbort()
        throw new S3UploadError(
            'unknown',
            `Server returned ${parts.length} presigned part URL(s) but ${slices.length} were expected`,
        )
    }

    const partsByNumber = new Map<number, UploadPartUrl>(parts.map(part => [part.part_number, part]))
    const loadedByPart = new Array<number>(slices.length).fill(0)
    const reportProgress = () => {
        if (!onProgress) return
        const totalLoaded = loadedByPart.reduce((sum, loaded) => sum + loaded, 0)
        onProgress(Math.round((totalLoaded / file.size) * 100))
    }

    const completedParts: CompletedPart[] = new Array(slices.length)

    // One controller for every part PUT so the first failure cancels the in-flight siblings and
    // stops the pool from pulling more slices; otherwise they keep uploading (and retrying) to an
    // upload_id we are about to abort. Linked to the caller's signal so user cancel does the same.
    const partsController = new AbortController()
    const onCallerAbort = () => partsController.abort()
    if (signal) {
        if (signal.aborted) {
            partsController.abort()
        } else {
            signal.addEventListener('abort', onCallerAbort)
        }
    }

    try {
        await runWithConcurrency(
            slices,
            getConcurrency(),
            async (slice, index) => {
                throwIfAborted()
                const partNumber = index + 1
                const partUrl = partsByNumber.get(partNumber)
                if (!partUrl) {
                    throw new S3UploadError('unknown', `No presigned URL returned for part ${partNumber}`)
                }
                try {
                    completedParts[index] = await uploadPartWithRetry(
                        slice,
                        partUrl,
                        loaded => {
                            loadedByPart[index] = loaded
                            reportProgress()
                        },
                        partsController.signal,
                    )
                } catch (error) {
                    partsController.abort()
                    throw error
                }
            },
            partsController.signal,
        )
    } catch (error) {
        await doAbort()
        if (error instanceof S3UploadError) {
            throw error
        }
        throw classifyApiError(error)
    } finally {
        if (signal) {
            signal.removeEventListener('abort', onCallerAbort)
        }
    }

    if (signal && signal.aborted) {
        await doAbort()
        throw new S3UploadError('aborted', 'Upload was aborted')
    }

    try {
        const completeResponse = await completeUpload({
            upload_id: uploadId,
            key,
            parts: completedParts,
        })
        if (!completeResponse.ok || !completeResponse.data || !completeResponse.data.data) {
            throw classifyApiError(completeResponse)
        }
        const data = completeResponse.data.data
        // The client knows what it just uploaded and where, so don't let the response override
        // that: FileUploader renders nothing unless resource_type is 'image' | 'video', and
        // buildMediaUrl only uses backup_url when source is 'aws'.
        const serverResourceType = data.resource_type
        return {
            ...data,
            source: 'aws',
            resource_type:
                serverResourceType === 'image' || serverResourceType === 'video' ? serverResourceType : contentType,
        }
    } catch (error) {
        await doAbort()
        if (error instanceof S3UploadError) {
            throw error
        }
        throw classifyApiError(error)
    }
}
